import { Suspense, lazy, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { NavLink, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useApp } from '../data/store.tsx';
import { useSession } from '../data/session.tsx';
import { TasteRefiner } from '../data/taste.tsx';
import { AgeGate } from './components/AgeGate.tsx';
import { AmbientArt } from './components/AmbientArt.tsx';
import { CommandPalette, openPalette } from './components/CommandPalette.tsx';
import { Glyph } from './components/Glyph.tsx';
import { SiteFooter } from './components/SiteFooter.tsx';
import { FooterHandle } from './components/FooterHandle.tsx';
import { Welcome, wasWelcomed } from './screens/Welcome.tsx';
import { Home } from './screens/Home.tsx';
import { ImprovePlan } from './components/ImprovePlan.tsx';
import { Profile } from './screens/Profile.tsx';
import { Triage } from './screens/Triage.tsx';
import { RateScreen } from './screens/Rate.tsx';
import { Priorities } from './screens/Priorities.tsx';
import { Schedule } from './screens/Schedule.tsx';
import { Conflicts } from './screens/Conflicts.tsx';
import { ConflictDetail } from './screens/ConflictDetail.tsx';
import { GroupView } from './screens/GroupView.tsx';
import { EventMode } from './screens/EventMode.tsx';
import { SharedPlan } from './screens/SharedPlan.tsx';
import { WeatherProvider } from '../data/weather.tsx';
import { WeatherNotice } from './components/WeatherNotice.tsx';
import PageMetadata, { PublicPageGuide } from './components/PageMetadata.tsx';

// Loaded on request rather than at startup. Export pulls in a PDF library, and
// none of these screens sits between opening the application and reading a
// schedule.
const Photos = lazy(() => import('./screens/Photos.tsx').then((m) => ({ default: m.Photos })));
const Forum = lazy(() => import('./screens/Forum.tsx').then((m) => ({ default: m.Forum })));
const Messages = lazy(() =>
  import('./screens/Messages.tsx').then((m) => ({ default: m.Messages })),
);
const Account = lazy(() => import('./screens/Account.tsx').then((m) => ({ default: m.Account })));
const SettingsScreen = lazy(() =>
  import('./screens/Settings.tsx').then((m) => ({ default: m.SettingsScreen })),
);
const Search = lazy(() => import('./screens/Search.tsx').then((m) => ({ default: m.Search })));
const Artists = lazy(() => import('./screens/Artists.tsx').then((m) => ({ default: m.Artists })));
const Preferences = lazy(() =>
  import('./screens/Preferences.tsx').then((m) => ({ default: m.Preferences })),
);
const Contributors = lazy(() =>
  import('./screens/Contributors.tsx').then((m) => ({ default: m.Contributors })),
);
const FeedbackScreen = lazy(() =>
  import('./screens/Feedback.tsx').then((m) => ({ default: m.Feedback })),
);
const Privacy = lazy(() => import('./screens/Pages.tsx').then((m) => ({ default: m.Privacy })));
const Resources = lazy(() => import('./screens/Pages.tsx').then((m) => ({ default: m.Resources })));
const Help = lazy(() => import('./screens/Help.tsx').then((m) => ({ default: m.Help })));
const Terms = lazy(() => import('./screens/Pages.tsx').then((m) => ({ default: m.Terms })));
const Weather = lazy(() => import('./screens/Weather.tsx').then((m) => ({ default: m.Weather })));
const News = lazy(() => import('./screens/News.tsx').then((m) => ({ default: m.News })));
const Timeline = lazy(() => import('./screens/Timeline.tsx').then((m) => ({ default: m.Timeline })));

const PRIMARY = [
  { to: '/home', label: 'Home' },
  { to: '/plan', label: 'Plan' },
  { to: '/lineup', label: 'Lineup' },
  { to: '/forum', label: 'Community' },
  { to: '/profile', label: 'Profile' },
];

const UPDATES = [
  { to: '/news', label: 'News' },
  { to: '/weather', label: 'Weather' },
];

function SiteHeader() {
  const location = useLocation();
  const { session } = useSession();
  const [openAt, setOpenAt] = useState<string | null>(null);
  const open = openAt === location.key;
  const header = useRef<HTMLElement>(null);
  const toggle = useRef<HTMLButtonElement>(null);

  useEffect(() => { setOpenAt(null); }, [location.key]);

  useEffect(() => {
    if (!open) return;
    const dismiss = (event: PointerEvent) => {
      if (event.target instanceof Node && !header.current?.contains(event.target)) setOpenAt(null);
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setOpenAt(null);
      toggle.current?.focus();
    };
    document.addEventListener('pointerdown', dismiss);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('pointerdown', dismiss);
      document.removeEventListener('keydown', escape);
    };
  }, [open]);

  return (
    <header className="topbar" ref={header}>
      <div className="topbar-row">
        <NavLink to="/home" className="brand" aria-label="Lost Lands home">
          Lost Lands<small>2026</small>
        </NavLink>
        <button type="button" className="topsearch" aria-label="Search" onClick={() => { setOpenAt(null); openPalette(); }}>
          <Glyph name="search" />
        </button>
        <button
          ref={toggle}
          type="button"
          className="nav-toggle"
          aria-expanded={open}
          aria-controls="site-navigation"
          onClick={() => setOpenAt(open ? null : location.key)}
        >
          <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false">
            <path d={open ? 'M6 6l12 12M6 18L18 6' : 'M4 6h16M4 12h16M4 18h16'} fill="none" stroke="currentColor" strokeWidth="2" />
          </svg>
          {open ? 'Close' : 'Menu'}
        </button>
        <div className="header-account"><AccountChip /></div>
        <div
          id="site-navigation"
          className="navigation-panel"
          data-open={open}
          onClick={(event) => {
            if (event.target instanceof Element && event.target.closest('a')) setOpenAt(null);
          }}
        >
          <nav className="primarynav" aria-label="Main navigation">
            {PRIMARY.map((item) => <NavLink key={item.to} to={item.to}>{item.label}</NavLink>)}
          </nav>
          <div className="navtools">
            <button className="jumpbtn" type="button" onClick={() => { setOpenAt(null); openPalette(); }}>
              <Glyph name="search" /><span>Search app</span><kbd>⌘K</kbd>
            </button>
            <nav className="toolnav" aria-label="Tools">
              <NavLink to="/search" aria-label="Find people and groups"><Glyph name="person" /><span>People</span></NavLink>
              <NavLink to="/messages"><Glyph name="message" /><span>Messages</span></NavLink>
              {!session?.user && <NavLink to="/settings"><Glyph name="gear" /><span>Settings</span></NavLink>}
            </nav>
          </div>
        </div>
      </div>
      <nav className="updates-subnav" aria-label="Festival updates">
        {UPDATES.map((item) => <NavLink key={item.to} to={item.to} onClick={() => setOpenAt(null)}>{item.label}</NavLink>)}
      </nav>
    </header>
  );
}

function AppGate({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const publicInformation = ['/news', '/weather', '/timeline', '/faq', '/links', '/privacy', '/terms'].includes(pathname);
  return publicInformation ? <>{children}</> : <AgeGate publicInfo={<PublicPageGuide />}>{children}</AgeGate>;
}

// Shows the signed-in initial, or a link to sign in, and nothing while the
// session answer is still on its way.
function AccountChip() {
  const { session, known } = useSession();
  const location = useLocation();
  const user = session?.user;

  if (!known) return null;
  if (!user) {
    return (
      <NavLink
        to="/account"
        state={{ from: `${location.pathname}${location.search}` }}
        className="signin"
      >
        Sign in
      </NavLink>
    );
  }
  return <AvatarMenu username={user.username} initial={(user.displayName || user.username).slice(0, 1).toUpperCase()} />;
}

// The account's own links behind the avatar. It is not a dialog, so focus is not
// trapped: Tab leaves it, and Escape or a tap elsewhere closes it. The panel is
// drawn in the document body because the top bar clips what overflows it.
function AvatarMenu({ username, initial }: { username: string; initial: string }) {
  const { logout } = useSession();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [place, setPlace] = useState<{ top: number; right: number }>({ top: 0, right: 0 });
  const button = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);

  useEffect(() => { setOpen(false); }, [location.key]);

  useEffect(() => {
    if (!open) return;
    const position = () => {
      const rect = button.current?.getBoundingClientRect();
      if (rect) setPlace({ top: rect.bottom + 6, right: Math.max(8, window.innerWidth - rect.right) });
    };
    position();
    const outside = (event: PointerEvent) => {
      if (event.target instanceof Node && !button.current?.contains(event.target) && !panel.current?.contains(event.target)) setOpen(false);
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setOpen(false);
      button.current?.focus();
    };
    window.addEventListener('resize', position);
    document.addEventListener('pointerdown', outside);
    document.addEventListener('keydown', escape);
    return () => {
      window.removeEventListener('resize', position);
      document.removeEventListener('pointerdown', outside);
      document.removeEventListener('keydown', escape);
    };
  }, [open]);

  return (
    <>
      <button
        ref={button}
        type="button"
        className="mechip"
        aria-expanded={open}
        aria-controls="account-menu"
        aria-label={`Account menu, signed in as ${username}`}
        title={`Signed in as ${username}`}
        onClick={() => setOpen((was) => !was)}
      >
        <span aria-hidden="true">{initial}</span>
      </button>
      {open && createPortal(
        <div ref={panel} id="account-menu" className="accountmenu" style={{ top: place.top, right: place.right }}>
          <NavLink to="/profile">Profile</NavLink>
          <NavLink to="/preferences">Festival preferences</NavLink>
          <NavLink to="/settings">Settings</NavLink>
          <button type="button" onClick={() => { setOpen(false); void logout(); }}>Sign out</button>
        </div>,
        document.body,
      )}
    </>
  );
}

const COMMUNITY = [
  { to: '/forum', label: 'Forum' },
  { to: '/search', label: 'People and groups' },
  { to: '/messages', label: 'Messages' },
  { to: '/photos', label: 'Photos' },
];

function CommunityNav() {
  const { pathname } = useLocation();
  if (!COMMUNITY.some((item) => item.to === pathname)) return null;
  return (
    <nav className="community-subnav" aria-label="Community">
      {COMMUNITY.map((item) => <NavLink key={item.to} to={item.to}>{item.label}</NavLink>)}
    </nav>
  );
}

// The schedule is on screen either way, so this is a strip across the top
// rather than a page, and it says the one thing that is different: nothing the
// person does is being kept.
function StorageBar() {
  const { storage } = useApp();
  return (
    <div className="storagebar">
      <span>
        Set times are showing, but this browser is not saving anything. A private window or
        blocked site data does that. Picks last until the tab closes.
      </span>
      <button className="small" onClick={() => void storage.destroy().then(() => window.location.reload())}>
        Reset this browser's copy
      </button>
    </div>
  );
}

export function App() {
  const { state, bootError } = useApp();
  const { session } = useSession();
  const location = useLocation();
  const reading = UPDATES.some((item) => item.to === location.pathname);
  const appClass = `app${reading ? ' app--reading' : ''}${location.pathname === '/news' ? ' app--news' : ''}${location.pathname === '/home' ? ' app--home' : ''}`;

  if (!state.ready) {
    return (
      <><PageMetadata /><AppGate>
        <div className={appClass}>
          <main>
            <p className="muted">Reading what is already on this device.</p>
          </main>
        </div>
      </AppGate></>
    );
  }

  const authPath = location.pathname === '/account' || location.pathname === '/reset';
  const bare =
    location.pathname === '/event' ||
    location.pathname === '/welcome' ||
    (authPath && !session?.user);

  return (
    <><PageMetadata /><AppGate>
    <WeatherProvider>
    <AmbientArt />
    <TasteRefiner />
    <div className={appClass}>
      <a className="skip-link" href="#main">
        Skip to content
      </a>

      {bootError && <StorageBar />}

      {!bare && <SiteHeader />}

      <main id="main" className={location.pathname === '/event' ? 'tight' : undefined}>
        {!authPath && location.pathname !== '/welcome' && location.pathname !== '/weather' && <WeatherNotice />}
        <CommunityNav />
        <Suspense fallback={<p className="muted">Loading that screen.</p>}>
        <Routes>
          <Route path="/" element={<Navigate to={wasWelcomed() ? '/home' : '/welcome'} replace />} />
          <Route path="/welcome" element={<Welcome />} />
          <Route path="/home" element={<Home />} />
          <Route path="/plan" element={<Schedule />} />
          <Route
            path="/plan/improve"
            element={
              <div className="narrowpage">
                <h1>Improve my plan</h1>
                <p><NavLink to="/plan">Back to My plan</NavLink></p>
                <ImprovePlan limit={12} />
              </div>
            }
          />
          <Route path="/preferences" element={<Preferences />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/search" element={<Search />} />

          {/* The old stages keep working and keep their behavior. They are no
              longer a pipeline anybody is walked through. */}
          <Route path="/lineup" element={<RateScreen />} />
          <Route path="/onboarding" element={<Navigate to="/preferences" replace />} />
          <Route path="/setup" element={<Navigate to="/preferences" replace />} />
          <Route path="/triage" element={<Triage />} />
          <Route path="/rate" element={<Navigate to="/lineup" replace />} />
          <Route path="/priorities" element={<Priorities />} />
          <Route path="/conflicts" element={<Conflicts />} />
          <Route path="/conflicts/:id" element={<ConflictDetail />} />
          <Route path="/group" element={<GroupView />} />
          <Route path="/photos" element={<Photos />} />
          <Route path="/messages" element={<Messages />} />
          <Route path="/forum" element={<Forum />} />
          <Route path="/account" element={<Account />} />
          <Route path="/reset" element={<Account />} />
          <Route path="/settings" element={<SettingsScreen />} />
          <Route path="/feedback" element={<FeedbackScreen />} />
          <Route path="/faq" element={<Help />} />
          <Route path="/links" element={<Resources />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/event" element={<EventMode />} />
          <Route path="/weather" element={<Weather />} />
          <Route path="/news" element={<News />} />
          <Route path="/timeline" element={<Timeline />} />
          <Route path="/shared" element={<SharedPlan />} />
          <Route path="/art" element={<Artists />} />
          <Route path="/art/contributors" element={<Contributors />} />

          <Route path="/schedule" element={<Navigate to="/plan" replace />} />
          <Route path="/crew" element={<Navigate to="/profile" replace />} />
          <Route path="/discover" element={<Navigate to="/search" replace />} />
          <Route path="/export" element={<Navigate to="/settings?section=transfer" replace />} />
          <Route path="/ingest" element={<Navigate to="/settings?section=transfer" replace />} />
          <Route path="/dev" element={<Navigate to="/settings" replace />} />
          <Route path="*" element={<section className="narrowpage"><h1>Page not found</h1><p>This link does not lead to a page in the app.</p><NavLink to="/news">News and updates</NavLink> · <NavLink to="/plan">Open the planner</NavLink></section>} />
        </Routes>
        </Suspense>
        <PublicPageGuide />
      </main>

      {!bare && <CommandPalette />}
      {!bare && <FooterHandle />}
      {!bare && <SiteFooter />}
    </div>
    </WeatherProvider>
    </AppGate></>
  );
}
